/**
 * Fog of war (`updateSeen` in the dungeon-floors prototype's
 * `dungeon-main.js`): presentation only; game-core has no visibility. From the
 * leader's tile, every tile within the radius whose straight grid ray meets
 * no wall before it is revealed (so the first wall is seen as a silhouette,
 * not the room behind it), and the whole room the leader stands in, with its
 * wall ring.
 *
 * The Off tier fades each unseen instance toward the fog colour (`batch.ts`).
 * The Medium tier draws the fog in the shader from a smooth exploration mask
 * instead (HD2D-STATUS gotcha: the fog tint belongs in the fog colour, not in
 * per-instance colours, which split along tile edges): one W×H RGBA8 texture
 * with bilinear filtering, R explored, G solid rock, B the rock-top ambient
 * share (`radiance.ts` fills it). The radiance hook samples it.
 */
import type { Floor, Pos } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { inRoom, isOpen, tileIndex } from './grid.ts';

export type ExplorationMask = {
  readonly texture: THREE.DataTexture;
  readonly data: Uint8Array;
  /** Writes the explored channel from the seen tiles. */
  update(seen: ReadonlySet<number>): void;
};

export const createExplorationMask = (floor: Floor): ExplorationMask => {
  const n = floor.width * floor.height;
  const data = new Uint8Array(n * 4);
  for (let i = 0; i < n; i += 1) {
    data[i * 4 + 1] = floor.tiles[i] === 'wall' ? 255 : 0;
    data[i * 4 + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, floor.width, floor.height);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return {
    texture,
    data,
    update(seen) {
      for (let i = 0; i < n; i += 1) data[i * 4] = seen.has(i) ? 255 : 0;
      texture.needsUpdate = true;
    },
  };
};

export const reveal = (floor: Floor, seen: Set<number>, p: Pos): void => {
  const { radius, window } = presentation.fog;
  for (let y = Math.max(0, p.y - window); y <= Math.min(floor.height - 1, p.y + window); y += 1) {
    for (let x = Math.max(0, p.x - window); x <= Math.min(floor.width - 1, p.x + window); x += 1) {
      if (Math.hypot(x - p.x, y - p.y) > radius) continue;
      const steps = Math.max(Math.abs(x - p.x), Math.abs(y - p.y));
      let clear = true;
      for (let n = 1; n < steps; n += 1) {
        if (!isOpen(floor, Math.round(p.x + ((x - p.x) * n) / steps), Math.round(p.y + ((y - p.y) * n) / steps))) {
          clear = false;
          break;
        }
      }
      if (clear) seen.add(tileIndex(floor, x, y));
    }
  }
  const room = floor.rooms.find((r) => inRoom(r, p.x, p.y));
  if (room === undefined) return;
  for (let y = Math.max(0, room.y - 1); y <= Math.min(floor.height - 1, room.y + room.h); y += 1) {
    for (let x = Math.max(0, room.x - 1); x <= Math.min(floor.width - 1, room.x + room.w); x += 1) seen.add(tileIndex(floor, x, y));
  }
};
